/**
 * VaultControl — deposit / redeem / claim in the console design language, wired
 * to the real on-chain vault (preflight, approval, receipt-waiting). Ported
 * from the design's VaultPage; whitelist/admin surfaces intentionally omitted.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { preflightDeposit, usePreviewDeposit, useTokenApproval, waitForVaultReceipt } from "@thema/ui";
import { useTokenBalance } from "@thema/ui/wallet";
import type { WorkspaceData } from "../components/VaultWorkspace";
import { PageHead, SectionTitle, Row, Badge, Callout } from "../components/primitives";
import { LiveAreaChart } from "../components/charts";
import { Icons } from "../components/icons";
import { SHARE_SYMBOL, POLL_INTERVAL_MS } from "../console-config";
import { formatAmount } from "../lib/format";

type Mode = "deposit" | "redeem" | "claim";
const ZERO = "0x0000000000000000000000000000000000000000" as `0x${string}`;

function describeWalletError(label: string, e: unknown): string {
  console.error(`[${label}] failed:`, e);
  const err = e as {
    shortMessage?: string;
    details?: string;
    metaMessages?: string[];
    cause?: { shortMessage?: string; message?: string };
    message?: string;
    name?: string;
  };
  if (err?.name === "UserRejectedRequestError") return "You rejected the transaction in your wallet.";
  return (
    err?.shortMessage ||
    err?.cause?.shortMessage ||
    (err?.metaMessages && err.metaMessages[0]) ||
    err?.cause?.message ||
    err?.details ||
    err?.message ||
    `${label} failed`
  );
}

export default function VaultControl({ ws }: { ws: WorkspaceData }) {
  const {
    vault,
    assetMeta,
    assetSymbol,
    accountingSymbol,
    assetDecimals,
    shareDecimals,
    assetAddress,
    isOnCorrectChain,
    capStatus,
    connected,
    vaultState,
    userPosition,
    writes,
    refetchAll,
    explorerBase,
  } = ws;

  const [mode, setMode] = useState<Mode>("deposit");
  const [amt, setAmt] = useState("");
  const [requestId, setRequestId] = useState("");
  const [phase, setPhase] = useState<"idle" | "working" | "done" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null);

  const reset = useCallback(() => {
    setAmt("");
    setRequestId("");
    setPhase("idle");
    setError(null);
    setTxHash(null);
  }, []);
  const switchMode = (m: Mode) => {
    setMode(m);
    reset();
  };

  // ── deposit-context reads (called unconditionally) ──────────────────────
  const remaining = capStatus?.maxDeposit ?? "0";
  const remainingWei = capStatus?.maxDepositWei ?? 0n;
  const depositEnabled = capStatus?.configured === true && remainingWei > 0n;

  const { shares: previewShares, isLoading: previewLoading } = usePreviewDeposit({
    vault,
    amount: mode === "deposit" ? amt : "",
    assetDecimals,
    shareDecimals,
    paused: phase === "working",
  });

  const { isApproved, approve, refetch: refetchApproval } = useTokenApproval({
    token: assetAddress ?? ZERO,
    spender: vault.vaultAddress,
    chainId: parseInt(vault.chainId, 10),
    requiredAmount: mode === "deposit" ? amt || "0" : "0",
    decimals: assetDecimals,
  });

  const {
    formatted: walletBalanceFormatted,
    balance: walletBalanceWei,
    refetch: refetchBalance,
  } = useTokenBalance({ token: assetAddress ?? ZERO, chainId: parseInt(vault.chainId, 10), account: connected });

  useEffect(() => {
    if (!connected) return;
    const id = setInterval(() => {
      void refetchBalance();
      void refetchApproval();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [connected, refetchBalance, refetchApproval]);

  // live share-price buffer for the right-column chart
  const [series, setSeries] = useState<number[]>([]);
  const lastRef = useRef<string | null>(null);
  useEffect(() => {
    if (!vaultState) return;
    const p = Number(vaultState.sharePrice);
    if (!Number.isFinite(p) || lastRef.current === vaultState.sharePrice) return;
    lastRef.current = vaultState.sharePrice;
    setSeries((s) => (s.length === 0 ? Array(28).fill(p) : [...s.slice(-63), p]));
  }, [vaultState?.sharePrice, vaultState]);

  // ── derived ─────────────────────────────────────────────────────────────
  const heldNum = Number(userPosition?.shareBalance ?? "0");
  const claimableNum = Number(userPosition?.claimableAssets ?? "0");
  const n = parseFloat(amt) || 0;
  const balanceNum = Number(walletBalanceFormatted ?? "0");

  const bal = mode === "deposit" ? balanceNum : heldNum;
  const balLabel =
    mode === "deposit"
      ? `${formatAmount(walletBalanceFormatted ?? "0", 6)} ${assetSymbol}`
      : mode === "redeem"
      ? `${formatAmount(userPosition?.shareBalance ?? "0", 6)} ${SHARE_SYMBOL}`
      : `${formatAmount(userPosition?.claimableAssets ?? "0", 8)} ${assetSymbol}`;

  const exceedsCap = mode === "deposit" && n > 0 && n > Number(remaining);
  const exceedsBalance = mode === "deposit" && n > 0 && walletBalanceFormatted !== null && n > balanceNum;
  const exceedsHeld = mode === "redeem" && n > 0 && n > heldNum;
  const needsApproval = mode === "deposit" && !!amt && !isApproved;

  const setMax = () => {
    if (mode === "deposit") {
      if (remainingWei === 0n) return;
      if (walletBalanceWei !== null && walletBalanceWei < remainingWei) setAmt(walletBalanceFormatted ?? "0");
      else setAmt(remaining);
    } else if (mode === "redeem") {
      if (userPosition) setAmt(userPosition.shareBalance);
    }
  };
  const setPct = (pct: number) => {
    if (mode === "claim") return;
    const base = mode === "deposit" ? Math.min(balanceNum, Number(remaining) || balanceNum) : heldNum;
    if (base > 0) setAmt(String(+(base * (pct / 100)).toFixed(mode === "deposit" ? assetDecimals : 6)));
  };

  // ── actions ───────────────────────────────────────────────────────────────
  const doDeposit = useCallback(async () => {
    if (!connected || !amt) return;
    setPhase("working");
    setError(null);
    setTxHash(null);
    try {
      if (needsApproval) {
        const { hash } = await approve();
        if (hash) {
          const r = await waitForVaultReceipt(vault, hash as `0x${string}`);
          if (r.status !== "success") throw new Error("Approval reverted on chain");
        }
        await refetchApproval();
        setPhase("idle");
        return;
      }
      const pre = await preflightDeposit({ vault, account: connected, amount: amt, assetDecimals, assetSymbol, receiver: connected });
      if (!pre.ok) {
        if (pre.error) console.error("[deposit] preflight:", pre.error);
        throw new Error(pre.reason ?? "Deposit would revert on chain");
      }
      const { hash } = await writes.deposit(amt, connected);
      const r = await waitForVaultReceipt(vault, hash as `0x${string}`);
      if (r.status !== "success") throw new Error("Deposit reverted on chain — see explorer");
      setTxHash(hash as `0x${string}`);
      setPhase("done");
      await Promise.all([refetchApproval(), refetchAll(), refetchBalance()]);
      setTimeout(reset, 2200);
    } catch (e) {
      setError(describeWalletError("deposit", e));
      setPhase("failed");
    }
  }, [connected, amt, needsApproval, approve, vault, assetDecimals, assetSymbol, writes, refetchApproval, refetchAll, refetchBalance, reset]);

  const doRedeem = useCallback(async () => {
    if (!connected || !amt) return;
    setPhase("working");
    setError(null);
    setTxHash(null);
    try {
      const { hash } = await writes.requestRedeem(amt, connected, connected);
      if (hash) setTxHash(hash as `0x${string}`);
      setPhase("done");
      await refetchAll();
      setTimeout(reset, 2200);
    } catch (e) {
      setError(describeWalletError("redeem", e));
      setPhase("failed");
    }
  }, [connected, amt, writes, refetchAll, reset]);

  const doClaim = useCallback(async () => {
    if (!connected || !requestId) return;
    setPhase("working");
    setError(null);
    setTxHash(null);
    try {
      const { hash } = await writes.claimRedeem(requestId, connected);
      if (hash) setTxHash(hash as `0x${string}`);
      setPhase("done");
      await refetchAll();
      setTimeout(reset, 2200);
    } catch (e) {
      setError(describeWalletError("claim", e));
      setPhase("failed");
    }
  }, [connected, requestId, writes, refetchAll, reset]);

  const submit = () => {
    if (!connected) return;
    if (mode === "deposit") void doDeposit();
    else if (mode === "redeem") void doRedeem();
    else void doClaim();
  };

  const canSubmit = useMemo(() => {
    if (!connected || phase === "working" || !isOnCorrectChain) return !connected; // allow connect path
    if (mode === "deposit") return !!amt && depositEnabled && !exceedsCap && !exceedsBalance;
    if (mode === "redeem") return !!amt && n > 0 && !exceedsHeld;
    return !!requestId && Number(requestId) > 0 && claimableNum > 0;
  }, [connected, phase, isOnCorrectChain, mode, amt, depositEnabled, exceedsCap, exceedsBalance, n, exceedsHeld, requestId, claimableNum]);

  const submitLabel = () => {
    if (!connected) return (<><Icons.wallet size={16} /> Connect wallet</>);
    if (phase === "working") return (<><Icons.spark size={16} className="spin" /> Confirming…</>);
    if (phase === "done") return (<><Icons.check size={16} /> Done</>);
    if (mode === "deposit") return needsApproval ? `Approve ${assetSymbol}` : `Deposit ${assetSymbol}`;
    if (mode === "redeem") return `Request redeem`;
    return `Claim ${assetSymbol}`;
  };

  const txUrl = txHash && explorerBase ? `${explorerBase}/tx/${txHash}` : undefined;

  return (
    <div className="page-entry">
      <PageHead
        eyebrow="Vault"
        title={assetMeta?.asset.symbol ? `${assetMeta.asset.symbol} Vault` : "Vault control"}
        sub="Deposit converts the asset to LP shares; redeem queues shares for the next settlement epoch; claim settles finalized requests."
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <Badge variant="live">live</Badge>
            <span className="chip" style={{ cursor: "default" }}>
              <Icons.globe size={13} /> {vault.chainName}
            </span>
          </div>
        }
      />

      {!isOnCorrectChain && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">Switch your wallet to {vault.chainName} before submitting.</Callout>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.2fr)", gap: 16 }}>
        {/* ── action card ─────────────────────────────────────────────── */}
        <div className="card" style={{ padding: 22 }}>
          {/* mode toggle */}
          <div style={{ display: "flex", background: "var(--surface-alt)", borderRadius: 10, padding: 4, marginBottom: 20 }}>
            {(["deposit", "redeem", "claim"] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                style={{
                  flex: 1,
                  padding: "9px 0",
                  borderRadius: 7,
                  cursor: "pointer",
                  border: "none",
                  fontFamily: "var(--mono)",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                  background: mode === m ? "var(--surface)" : "transparent",
                  color: mode === m ? "var(--accent)" : "var(--text-soft)",
                  boxShadow: mode === m ? "var(--card-shadow)" : "none",
                  transition: "all 0.16s var(--smooth)",
                }}
              >
                {m}
              </button>
            ))}
          </div>

          {mode !== "claim" ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span className="eyebrow">{mode === "deposit" ? "You deposit" : "Shares to redeem"}</span>
                <span className="mono" style={{ fontSize: 11.5, color: "var(--micro)" }}>Balance: {balLabel}</span>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="input"
                  style={{ height: 58, fontSize: 24, fontFamily: "var(--mono)", paddingRight: 120 }}
                  placeholder="0.00"
                  value={amt}
                  onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ""))}
                  disabled={phase === "working"}
                />
                <div style={{ position: "absolute", right: 12, top: 11, display: "flex", gap: 6, alignItems: "center" }}>
                  <button className="btn btn-soft btn-sm" onClick={setMax} disabled={phase === "working"}>Max</button>
                  <span className="mono" style={{ fontSize: 13, color: "var(--text-soft)", fontWeight: 600 }}>{mode === "deposit" ? assetSymbol : SHARE_SYMBOL}</span>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, margin: "10px 0 18px" }}>
                {[25, 50, 75, 100].map((pct) => (
                  <button
                    key={pct}
                    className="mono"
                    onClick={() => setPct(pct)}
                    style={{ flex: 1, padding: "7px 0", fontSize: 11, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-soft)", cursor: "pointer" }}
                  >
                    {pct}%
                  </button>
                ))}
              </div>

              <div style={{ background: "var(--surface-alt)", borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 9, marginBottom: 18 }}>
                <Row k="NAV per share" v={vaultState ? `${formatAmount(vaultState.sharePrice, 6)} ${assetSymbol}` : "…"} />
                {mode === "deposit" ? (
                  <Row
                    k="You receive (est.)"
                    v={previewShares === null ? (previewLoading ? "Calculating…" : "—") : `${formatAmount(previewShares, 6)} ${SHARE_SYMBOL}`}
                    strong
                  />
                ) : (
                  <Row k="Queued for epoch" v={amt ? `${formatAmount(amt, 6)} ${SHARE_SYMBOL}` : "—"} strong />
                )}
              </div>

              {mode === "deposit" && !depositEnabled && (
                <div style={{ marginBottom: 18 }}>
                  <Callout tone="warn">No remaining deposit headroom for this wallet (cap not configured or fully used). Redeem &amp; Claim still work.</Callout>
                </div>
              )}
              {exceedsCap && (
                <div style={{ marginBottom: 18 }}>
                  <Callout tone="error">That's more than you can deposit now (max {formatAmount(remaining, 4)} {assetSymbol}).</Callout>
                </div>
              )}
              {exceedsBalance && !exceedsCap && (
                <div style={{ marginBottom: 18 }}>
                  <Callout tone="error">Amount exceeds your wallet balance.</Callout>
                </div>
              )}
              {exceedsHeld && (
                <div style={{ marginBottom: 18 }}>
                  <Callout tone="error">Amount exceeds shares held ({formatAmount(userPosition?.shareBalance ?? "0", 6)} {SHARE_SYMBOL}).</Callout>
                </div>
              )}
            </>
          ) : (
            // ── claim ──────────────────────────────────────────────────
            <>
              <div style={{ background: "var(--surface-alt)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div className="eyebrow">Currently claimable</div>
                <div className="serif tnum" style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: "var(--teal)" }}>
                  {formatAmount(userPosition?.claimableAssets ?? "0", 8)} <span style={{ fontSize: 15, color: "var(--text-soft)" }}>{assetSymbol}</span>
                </div>
                {claimableNum <= 0 && userPosition && (
                  <div style={{ fontSize: 11.5, color: "var(--micro)", marginTop: 6 }}>
                    Nothing finalized yet. Pending shares: {formatAmount(userPosition.pendingShares, 6)}. Auto-refreshes every 15s.
                  </div>
                )}
              </div>

              {userPosition && userPosition.requestIds.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>Your request IDs</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {userPosition.requestIds.map((id) => (
                      <button
                        key={id}
                        className="chip"
                        onClick={() => setRequestId(String(id))}
                        style={{
                          borderColor: requestId === String(id) ? "var(--accent)" : "var(--border)",
                          background: requestId === String(id) ? "var(--accent-soft)" : "var(--surface)",
                          color: requestId === String(id) ? "var(--accent)" : "var(--text-soft)",
                          fontFamily: "var(--mono)",
                        }}
                      >
                        #{id}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="eyebrow" style={{ marginBottom: 8 }}>Request ID</div>
              <input
                className="input"
                style={{ height: 50, fontFamily: "var(--mono)", marginBottom: 18 }}
                placeholder="1"
                value={requestId}
                onChange={(e) => setRequestId(e.target.value.replace(/[^0-9]/g, ""))}
                disabled={phase === "working"}
              />
            </>
          )}

          <button className="btn btn-primary" style={{ width: "100%", height: 50 }} disabled={!canSubmit} onClick={submit}>
            {submitLabel()}
          </button>

          {phase === "done" && txHash && (
            <div style={{ marginTop: 12 }}>
              <Callout tone="success">
                Confirmed.{" "}
                {txUrl ? (
                  <a href={txUrl} target="_blank" rel="noreferrer" className="link mono" style={{ fontSize: 12 }}>
                    view tx
                  </a>
                ) : null}
              </Callout>
            </div>
          )}
          {phase === "failed" && error && (
            <div style={{ marginTop: 12 }}>
              <Callout tone="error">{error}</Callout>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginTop: 12 }}>
            <Icons.shield size={13} style={{ color: "var(--green)" }} />
            <span className="mono" style={{ fontSize: 11, color: "var(--micro)" }}>Safe custody · on-chain settlement</span>
          </div>
        </div>

        {/* ── vault analytics ─────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div className="eyebrow">Share price · live</div>
                <div className="serif tnum" style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }}>{vaultState ? formatAmount(vaultState.sharePrice, 6) : "—"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="eyebrow">TVL</div>
                <div className="serif tnum" style={{ fontSize: 26, fontWeight: 700, color: "var(--pos)" }}>{vaultState ? formatAmount(vaultState.totalAssets, 4) : "—"}</div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              {series.length > 1 ? (
                <LiveAreaChart data={series} color="var(--accent)" height={170} valueFmt={(v) => formatAmount(v, 6)} unit={` ${assetSymbol}`} />
              ) : (
                <div style={{ height: 170, display: "grid", placeItems: "center", color: "var(--micro)", fontSize: 13 }}>Reading vault…</div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="card" style={{ padding: 20 }}>
              <SectionTitle>Vault stats</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <Row k="Total value locked" v={vaultState ? `${formatAmount(vaultState.totalAssets, 6)} ${assetSymbol}` : "…"} />
                <Row k="Shares issued" v={vaultState ? formatAmount(vaultState.totalSupply, 2) : "…"} />
                <Row k="Underlying" v={assetSymbol || "…"} />
                <Row k="Accounting" v={accountingSymbol || "…"} />
                <Row k="Standard" v="ERC-4626" />
              </div>
            </div>
            <div className="card" style={{ padding: 20 }}>
              <SectionTitle>Your position</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <Row k="Held" v={`${formatAmount(userPosition?.shareBalance ?? "0", 6)}`} />
                <Row k="Pending" v={`${formatAmount(userPosition?.pendingShares ?? "0", 6)}`} />
                <Row k="Claimable" v={`${formatAmount(userPosition?.claimableAssets ?? "0", 8)} ${assetSymbol}`} />
                <Row k="Open requests" v={String(userPosition?.requestIds.length ?? 0)} />
                {capStatus?.configured && <Row k="Headroom" v={`${formatAmount(capStatus.maxDeposit, 4)} ${assetSymbol}`} strong />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
